import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, History, Download, Target, TrendingUp, DollarSign, Calendar, Package, LogOut, Home, Tag, Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import PartsCatalogPanel from '../../auto-spares/PartsCatalog';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SalesmanDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [todaySales, setTodaySales] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showTargetDialog, setShowTargetDialog] = useState(false);
  const [showAddSale, setShowAddSale] = useState(false);
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesHistoryQuery, setSalesHistoryQuery] = useState('');
  const [loading, setLoading] = useState(true);
  // NEW: multi-line ring-up (up to 10 lines per sale ticket)
  const MAX_SALE_LINES = 10;
  const blankLine = () => ({ _key: crypto.randomUUID(), part_description: '', quantity: 1, unit_price: '', _search: '', _open: false });
  const [saleLines, setSaleLines] = useState([blankLine()]);
  const [salePaymentMethod, setSalePaymentMethod] = useState('cash');
  const [saleCustomerName, setSaleCustomerName] = useState('');
  // Searchable parts-catalog combobox for the Add Sale dialog
  const [catalogParts, setCatalogParts] = useState([]);
  const [monthlyTarget, setMonthlyTarget] = useState('');
  const [salaryTarget, setSalaryTarget] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const isSalesman = user?.role === 'auto_spares_salesman';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const fetchDashboard = async () => {
    try {
      const myData = await axios.get(`${API_URL}/api/auto-spares/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(myData.data);
      setTodaySales(myData.data.today_sales || []);
      setMonthlyTarget(myData.data.monthly_target || '');
      setSalaryTarget(myData.data.salary_target || '');
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesHistory = async () => {
    try {
      const myHistory = await axios.get(`${API_URL}/api/auto-spares/sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesHistory(myHistory.data);
    } catch (error) {
      toast.error('Failed to load sales history');
    }
  };

  useEffect(() => {
    fetchDashboard();
    // Load catalog once for the typeable search dropdown
    axios.get(`${API_URL}/api/auto-spares/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCatalogParts(Array.isArray(res.data) ? res.data : (res.data.parts || [])))
      .catch(() => {});
  }, []);

  const handleAddSale = async () => {
    // Validate every line
    const customer = (saleCustomerName || '').trim();
    if (!customer) {
      toast.error('Customer name is required');
      return;
    }
    const cleaned = saleLines.map(l => ({
      part_description: (l.part_description || '').trim(),
      quantity: parseInt(l.quantity, 10) || 1,
      unit_price: parseFloat(l.unit_price) || 0,
      idempotency_key: l._key || crypto.randomUUID(),
    })).filter(l => l.part_description && l.unit_price > 0);
    if (!cleaned.length) {
      toast.error('Add at least one valid line (part + price)');
      return;
    }
    try {
      await Promise.all(cleaned.map(line =>
        axios.post(`${API_URL}/api/auto-spares/sales`, {
          part_description: line.part_description,
          quantity: line.quantity,
          unit_price: line.unit_price,
          payment_method: salePaymentMethod || 'cash',
          customer_name: customer,
          idempotency_key: line.idempotency_key,
        }, { headers: { Authorization: `Bearer ${token}` } })
      ));
      toast.success(cleaned.length === 1 ? 'Sale added!' : `${cleaned.length} sale lines added!`);
      setSaleLines([blankLine()]);
      setSalePaymentMethod('cash');
      setSaleCustomerName('');
      setShowAddSale(false);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to add sale');
    }
  };

  // Helpers for the multi-line ring-up dialog
  const updateLine = (idx, patch) => setSaleLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setSaleLines(prev => prev.length >= MAX_SALE_LINES ? prev : [...prev, blankLine()]);
  const removeLine = (idx) => setSaleLines(prev => prev.length <= 1 ? [blankLine()] : prev.filter((_, i) => i !== idx));

  const handleSetTarget = async () => {
    try {
      await axios.put(`${API_URL}/api/auto-spares/target`, {
        monthly_target: parseFloat(monthlyTarget) || 0,
        salary_target: parseFloat(salaryTarget) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Target updated!');
      setShowTargetDialog(false);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update target');
    }
  };

  // Calculate sales needed to reach salary target
  const commissionRate = dashboardData?.commission_rate || 10;
  const basicSalary = dashboardData?.basic_salary || 2000;
  const currentSalaryTarget = parseFloat(salaryTarget) || 0;
  
  // Commission needed = Salary Target - Basic Salary
  const commissionNeeded = Math.max(0, currentSalaryTarget - basicSalary);
  // Sales needed = Commission Needed / (Commission Rate / 100)
  const salesNeeded = commissionRate > 0 ? commissionNeeded / (commissionRate / 100) : 0;
  // Remaining to sell = Sales Needed - Month to Date
  const remainingToSell = Math.max(0, salesNeeded - (dashboardData?.month_to_date || 0));

  const downloadStatement = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auto-spares/statement`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const monthTag = new Date().toISOString().slice(0, 7);
      const safeName = (user?.full_name || 'salesman').replace(/\s+/g, '_');
      link.href = url;
      link.setAttribute('download', `JSM_Salary_Statement_${safeName}_${monthTag}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Salary statement downloaded!');
    } catch (error) {
      toast.error('Failed to download statement');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  };

  const totalNewSale = saleLines.reduce((s, l) => s + ((parseFloat(l.unit_price) || 0) * (parseInt(l.quantity, 10) || 1)), 0);
  const targetProgress = dashboardData?.monthly_target > 0 
    ? Math.min(100, (dashboardData?.month_to_date / dashboardData?.monthly_target) * 100)
    : 0;

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      {/* Header */}
      <div className="relative text-center mb-8">
        <div className="absolute right-0 top-0 flex gap-2">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white"
            title="Back to main menu"
            data-testid="salesman-home-btn"
          >
            <Home className="h-4 w-4 mr-2" /> Home
          </Button>
          <Button
            onClick={() => navigate('/auto-spares/parts-catalog')}
            variant="outline"
            className="border-cyan-500 text-cyan-300 hover:bg-cyan-600 hover:text-white"
            title="Browse the price catalog & upload product images"
            data-testid="salesman-catalog-btn"
          >
            <Package className="h-4 w-4 mr-2" /> Parts Catalog
          </Button>
          <Button
            onClick={() => navigate('/auto-spares/price-guide')}
            variant="outline"
            className="border-amber-500 text-amber-300 hover:bg-amber-600 hover:text-white"
            title="JSM master selling-price list with live stock"
            data-testid="salesman-price-guide-btn"
          >
            <Package className="h-4 w-4 mr-2" /> Price Guide
          </Button>
          <Button onClick={handleLogout} variant="outline" className="border-rose-600 text-rose-400 hover:bg-rose-600 hover:text-white" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">JSM AUTO SPARES</h1>
        <p className="text-xl text-emerald-400">Welcome, {dashboardData?.salesman_name || user.full_name || 'Salesman'}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5" />
              <span className="text-sm opacity-80">Today Sales</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(dashboardData?.today_total)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5" />
              <span className="text-sm opacity-80">Month to Date</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(dashboardData?.month_to_date)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-600 to-amber-700 border-0 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5" />
              <span className="text-sm opacity-80">Commission ({dashboardData?.commission_rate || 10}%)</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(dashboardData?.commission)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5" />
              <span className="text-sm opacity-80">Basic Salary</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(dashboardData?.basic_salary)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-600 to-rose-700 border-0 text-white col-span-2 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5" />
              <span className="text-sm opacity-80">Estimated Pay</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(dashboardData?.estimated_pay)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Payment Method Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="salesman-pm-breakdown">
        {[
          { k: 'cash',   label: 'Cash',   bg: 'from-emerald-600 to-emerald-700' },
          { k: 'online', label: 'Online', bg: 'from-blue-600 to-blue-700' },
          { k: 'eft',    label: 'EFT',    bg: 'from-purple-600 to-purple-700' },
          { k: 'card',   label: 'Card',   bg: 'from-amber-600 to-amber-700' },
        ].map(opt => {
          const amt = dashboardData?.today_breakdown?.[opt.k] || 0;
          const mtdAmt = dashboardData?.mtd_breakdown?.[opt.k] || 0;
          return (
            <Card key={opt.k} className={`bg-gradient-to-br ${opt.bg} border-0 text-white`} data-testid={`salesman-pm-tile-${opt.k}`}>
              <CardContent className="p-3">
                <div className="text-xs opacity-80 uppercase tracking-wide">{opt.label} Today</div>
                <p className="text-xl font-bold">{formatCurrency(amt)}</p>
                <div className="text-[10px] opacity-70 mt-0.5">MTD: {formatCurrency(mtdAmt)}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Allowance + Incentive + Loan + Target Bonus cards */}
      {((dashboardData?.allowance || 0) > 0 || (dashboardData?.overtime || 0) > 0 || (dashboardData?.incentive || 0) > 0 || (dashboardData?.loan_balance || 0) > 0 || (dashboardData?.target_bonus || 0) > 0) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="extra-pay-cards">
          {(dashboardData?.allowance || 0) > 0 && (
            <Card className="bg-gradient-to-br from-cyan-600 to-cyan-700 border-0 text-white" data-testid="allowance-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5" />
                  <span className="text-sm opacity-80">Allowance</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData.allowance)}</p>
              </CardContent>
            </Card>
          )}
          {(dashboardData?.overtime || 0) > 0 && (
            <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 text-white" data-testid="overtime-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5" />
                  <span className="text-sm opacity-80">Overtime</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData.overtime)}</p>
              </CardContent>
            </Card>
          )}
          {(dashboardData?.incentive || 0) > 0 && (
            <Card className="bg-gradient-to-br from-amber-600 to-amber-700 border-0 text-white" data-testid="incentive-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5" />
                  <span className="text-sm opacity-80">Incentive</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData.incentive)}</p>
                {dashboardData.incentive_note && (
                  <p className="text-xs opacity-80 mt-1">{dashboardData.incentive_note}</p>
                )}
              </CardContent>
            </Card>
          )}
          {(dashboardData?.target_bonus || 0) > 0 && (
            <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 text-white" data-testid="target-bonus-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-5 w-5" />
                  <span className="text-sm opacity-80">Target Bonus 🎯</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData.target_bonus)}</p>
                <p className="text-xs opacity-80 mt-1">{dashboardData.target_bonus_rate}% of R {(dashboardData.monthly_target || 0).toLocaleString()} target HIT</p>
              </CardContent>
            </Card>
          )}
          {(dashboardData?.loan_balance || 0) > 0 && (
            <Card className="bg-gradient-to-br from-rose-600 to-rose-800 border-0 text-white" data-testid="loan-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5" />
                  <span className="text-sm opacity-80">Staff Loan</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData.loan_balance)}</p>
                <div className="text-xs opacity-90 mt-1 space-y-0.5">
                  {(dashboardData?.loan_deduction || 0) > 0 && (
                    <p>This period: <span className="font-semibold">- {formatCurrency(dashboardData.loan_deduction)}</span></p>
                  )}
                  <p>Remaining after: <span className="font-semibold">{formatCurrency(dashboardData.loan_balance_after)}</span></p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* STILL NEED TO SELL Banner */}
      {currentSalaryTarget > 0 && (
        <Card data-testid="still-need-to-sell-card" className={`border-0 mb-8 ${remainingToSell > 0 ? 'bg-gradient-to-r from-rose-700 via-rose-600 to-orange-600' : 'bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600'}`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-white/80 text-sm font-medium uppercase tracking-wider">
                  {remainingToSell > 0 ? 'Still Need to Sell' : 'Target Achieved!'}
                </p>
                <p data-testid="still-need-to-sell-amount" className="text-4xl md:text-5xl font-black text-white mt-1">
                  {formatCurrency(remainingToSell)}
                </p>
              </div>
              <div className="text-center md:text-right text-white/70 text-sm space-y-1">
                <p>Salary Goal: <span className="text-white font-semibold">{formatCurrency(currentSalaryTarget)}</span></p>
                <p>Sales Required: <span className="text-white font-semibold">{formatCurrency(salesNeeded)}</span></p>
                <p>Already Sold: <span className="text-white font-semibold">{formatCurrency(dashboardData?.month_to_date || 0)}</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Target Progress */}
      {dashboardData?.monthly_target > 0 && (
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-white flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-400" />
                Monthly Target Progress
              </span>
              <span className="text-emerald-400 font-bold">
                {formatCurrency(dashboardData?.month_to_date)} / {formatCurrency(dashboardData?.monthly_target)}
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-4">
              <div 
                className={`h-4 rounded-full transition-all ${targetProgress >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${targetProgress}%` }}
              />
            </div>
            <p className="text-right text-sm text-slate-400 mt-1">{targetProgress.toFixed(1)}% achieved</p>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-8 justify-center">
        {isSalesman && (
          <div className="w-full bg-amber-950/40 border border-amber-700 rounded-lg p-4 flex items-start gap-3" data-testid="salesman-readonly-notice">
            <Search className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-200 font-semibold text-sm">Read-only access</p>
              <p className="text-amber-300/80 text-xs mt-1">
                You can search stock and check the last selling price for every product. Ring-ups happen at the till — ask an admin or a ring-up clerk to book the sale.
              </p>
              <Button
                onClick={() => navigate('/auto-spares/price-guide')}
                className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                size="sm"
                data-testid="salesman-open-price-guide"
              >
                <Tag className="h-4 w-4 mr-1" /> Open Price Guide
              </Button>
            </div>
          </div>
        )}
        {!isSalesman && (
        <Dialog open={showAddSale} onOpenChange={(o) => { setShowAddSale(o); if (!o) { setSaleLines([blankLine()]); setSalePaymentMethod('cash'); setSaleCustomerName(''); } }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3" data-testid="add-sale-btn">
              <Plus className="h-5 w-5 mr-2" /> Add Sale
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5" /> RING UP A SALE <span className="text-xs text-slate-400 font-normal">(up to {MAX_SALE_LINES} lines)</span></DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300">Customer Name *</Label>
                <Input
                  value={saleCustomerName}
                  onChange={(e) => setSaleCustomerName(e.target.value)}
                  placeholder="e.g., John Smith, Walk-in"
                  className="bg-slate-700 border-slate-600"
                  data-testid="salesman-sale-customer-name-input"
                />
              </div>
              {saleLines.map((line, idx) => (
                <div key={idx} className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2" data-testid={`sale-line-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-400">Line {idx + 1}</span>
                    {saleLines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="text-rose-400 hover:text-rose-300 text-xs"
                        data-testid={`sale-line-remove-${idx}`}
                      >Remove</button>
                    )}
                  </div>
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-300 text-xs">Search Catalog</Label>
                      {line.part_description && (
                        <a
                          href={`/auto-spares/parts-catalog?search=${encodeURIComponent((line.part_description.split('—')[0] || line.part_description).trim())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] text-cyan-400 hover:text-cyan-300 underline"
                          data-testid={`salesman-open-catalog-${idx}`}
                        >Open in catalog (upload image) ↗</a>
                      )}
                    </div>
                    <Input
                      value={line._search || ''}
                      onFocus={() => updateLine(idx, { _open: true })}
                      onChange={(e) => updateLine(idx, { _search: e.target.value, _open: true })}
                      placeholder="Type 'door', 'brake', 'JSM-BRK-001'…"
                      className="bg-slate-700 border-slate-600 h-9"
                      autoComplete="off"
                      data-testid={`salesman-catalog-search-${idx}`}
                    />
                    {line._open && (line._search || '').trim().length > 0 && (() => {
                      const q = (line._search || '').trim().toLowerCase();
                      const matches = catalogParts.filter(p =>
                        (p.supplier_sku || p.sku || p.master_code || '').toLowerCase().includes(q) ||
                        (p.name || '').toLowerCase().includes(q) ||
                        (p.category || '').toLowerCase().includes(q)
                      ).slice(0, 30);
                      return (
                        <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                          {matches.length === 0 ? (
                            <div className="px-3 py-3 text-slate-400 text-sm text-center">No matches — type description manually below.</div>
                          ) : matches.map(p => {
                            const code = p.supplier_sku || p.master_code || p.sku || '';
                            const price = p.selling_price ?? p.sale_price ?? 0;
                            return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => updateLine(idx, {
                                part_description: `${code} — ${p.name}`,
                                unit_price: price ? String(price) : '',
                                product_id: p.id || '',
                                _search: `${code} — ${p.name}`,
                                _open: false,
                              })}
                              className="w-full text-left px-3 py-2 hover:bg-slate-700 flex items-center gap-2 border-b border-slate-700 last:border-0"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-[11px] text-slate-400">{code}</div>
                                <div className="text-sm text-white truncate">{p.name}</div>
                                <div className="text-[10px] text-slate-500">
                                  {p.category}
                                  {typeof p.quantity === 'number' ? ` • stock ${p.quantity}` : ''}
                                </div>
                              </div>
                              <div className="text-emerald-400 font-bold text-sm shrink-0">{formatCurrency(price)}</div>
                            </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Part Description *</Label>
                    <Input
                      value={line.part_description}
                      onChange={(e) => updateLine(idx, { part_description: e.target.value })}
                      placeholder="Auto-filled from search, or type manually"
                      className="bg-slate-700 border-slate-600 h-9"
                      data-testid={`salesman-sale-part-input-${idx}`}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-300 text-xs">Qty</Label>
                      <Input
                        type="number" min="1"
                        value={line.quantity}
                        onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                        className="bg-slate-700 border-slate-600 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">Price (R) *</Label>
                      <Input
                        type="number" step="0.01"
                        value={line.unit_price}
                        onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                        placeholder="0.00"
                        className="bg-slate-700 border-slate-600 h-9"
                        data-testid={`salesman-sale-price-input-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">Line Total</Label>
                      <div className="h-9 px-3 flex items-center rounded-md bg-slate-700/60 border border-slate-600 text-emerald-400 font-semibold text-sm">
                        {formatCurrency((parseFloat(line.unit_price) || 0) * (parseInt(line.quantity, 10) || 1))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <Button
                type="button"
                onClick={addLine}
                disabled={saleLines.length >= MAX_SALE_LINES}
                className="w-full bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="salesman-add-sale-line-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                {saleLines.length >= MAX_SALE_LINES ? `Max ${MAX_SALE_LINES} lines reached` : `Add another line (${saleLines.length}/${MAX_SALE_LINES})`}
              </Button>

              <div>
                <Label className="text-slate-300">Payment Method *</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {[
                    { v: 'cash',   label: 'Cash',   cls: 'bg-emerald-600 hover:bg-emerald-700 border-emerald-500' },
                    { v: 'online', label: 'Online', cls: 'bg-blue-600 hover:bg-blue-700 border-blue-500' },
                    { v: 'eft',    label: 'EFT',    cls: 'bg-purple-600 hover:bg-purple-700 border-purple-500' },
                    { v: 'card',   label: 'Card',   cls: 'bg-amber-600 hover:bg-amber-700 border-amber-500' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setSalePaymentMethod(opt.v)}
                      data-testid={`salesman-sale-pm-${opt.v}`}
                      className={`rounded-md border px-2 py-2 text-sm font-semibold text-white transition ${salePaymentMethod === opt.v ? opt.cls + ' ring-2 ring-white/40' : 'bg-slate-700 border-slate-600 hover:bg-slate-600'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-slate-700 p-3 rounded-lg flex items-center justify-between">
                <p className="text-sm text-slate-400">GRAND TOTAL ({saleLines.length} {saleLines.length === 1 ? 'line' : 'lines'}):</p>
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(totalNewSale)}</p>
              </div>
              <Button onClick={handleAddSale} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="salesman-save-sale-btn">SAVE SALE</Button>
            </div>
          </DialogContent>
        </Dialog>
        )}

        <Dialog open={showHistory} onOpenChange={(open) => { setShowHistory(open); if (open) fetchSalesHistory(); }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-700 px-6 py-3" data-testid="view-history-btn">
              <History className="h-5 w-5 mr-2" /> View Sales History
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>My Sales History (This Month)</DialogTitle>
            </DialogHeader>
            <div className="mb-3">
              <Input
                value={salesHistoryQuery}
                onChange={(e) => setSalesHistoryQuery(e.target.value)}
                placeholder="Search by part description or customer name…"
                className="bg-slate-700 border-slate-600"
                data-testid="salesman-history-search-input"
              />
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Customer</TableHead>
                  <TableHead className="text-slate-400">Part</TableHead>
                  <TableHead className="text-slate-400 text-right">Qty</TableHead>
                  <TableHead className="text-slate-400 text-right">Unit Price</TableHead>
                  <TableHead className="text-slate-400 text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesHistory
                  .filter(sale => {
                    const q = salesHistoryQuery.trim().toLowerCase();
                    if (!q) return true;
                    return (sale.part_description || '').toLowerCase().includes(q)
                        || (sale.customer_name || '').toLowerCase().includes(q);
                  })
                  .map((sale, idx) => (
                  <TableRow key={idx} className="border-slate-700">
                    <TableCell>{new Date(sale.date).toLocaleDateString()}</TableCell>
                    <TableCell className="text-cyan-400">{sale.customer_name || '-'}</TableCell>
                    <TableCell>{sale.part_description}</TableCell>
                    <TableCell className="text-right">{sale.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(sale.unit_price)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(sale.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>

        <Button onClick={downloadStatement} variant="outline" className="border-slate-600 text-white hover:bg-slate-700 px-6 py-3" data-testid="download-statement-btn">
          <Download className="h-5 w-5 mr-2" /> Download Statement
        </Button>

        <Dialog open={showTargetDialog} onOpenChange={setShowTargetDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-amber-600 text-amber-400 hover:bg-amber-600/20 px-6 py-3" data-testid="set-target-btn">
              <Target className="h-5 w-5 mr-2" /> Set Target
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Set Salary & Sales Target</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="text-sm text-slate-400 mb-1">Your Current Setup:</p>
                <div className="flex justify-between text-sm">
                  <span>Basic Salary:</span>
                  <span className="text-emerald-400 font-bold">{formatCurrency(basicSalary)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Commission Rate:</span>
                  <span className="text-amber-400 font-bold">{commissionRate}%</span>
                </div>
              </div>
              
              <div>
                <Label className="text-amber-400 font-semibold">Desired Monthly Salary (R)</Label>
                <Input
                  type="number"
                  value={salaryTarget}
                  onChange={(e) => setSalaryTarget(e.target.value)}
                  placeholder="e.g., 10000"
                  className="bg-slate-700 border-slate-600 text-lg"
                />
                <p className="text-xs text-slate-400 mt-1">How much do you want to earn this month?</p>
              </div>
              
              {parseFloat(salaryTarget) > 0 && (
                <div className="bg-gradient-to-br from-emerald-900/50 to-blue-900/50 p-4 rounded-lg border border-emerald-600/30">
                  <p className="text-emerald-400 font-bold mb-3 text-center">TO EARN {formatCurrency(parseFloat(salaryTarget))}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-300">Basic Salary:</span>
                      <span className="text-white">{formatCurrency(basicSalary)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">+ Commission Needed:</span>
                      <span className="text-amber-400 font-bold">{formatCurrency(commissionNeeded)}</span>
                    </div>
                    <div className="border-t border-slate-600 my-2"></div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Sales Required ({commissionRate}%):</span>
                      <span className="text-blue-400 font-bold">{formatCurrency(salesNeeded)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-300">Already Sold:</span>
                      <span className="text-emerald-400">{formatCurrency(dashboardData?.month_to_date || 0)}</span>
                    </div>
                    <div className="border-t border-slate-600 my-2"></div>
                    <div className="flex justify-between text-lg">
                      <span className="text-white font-bold">STILL NEED TO SELL:</span>
                      <span className={`font-bold ${remainingToSell > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                        {formatCurrency(remainingToSell)}
                      </span>
                    </div>
                  </div>
                  
                  {remainingToSell <= 0 && (
                    <div className="mt-3 bg-emerald-600/20 p-2 rounded text-center">
                      <span className="text-emerald-400 font-bold">🎉 TARGET ACHIEVED!</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="border-t border-slate-700 pt-4">
                <Label className="text-slate-400">Monthly Sales Target (Optional)</Label>
                <Input
                  type="number"
                  value={monthlyTarget}
                  onChange={(e) => setMonthlyTarget(e.target.value)}
                  placeholder="e.g., 50000"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <Button onClick={handleSetTarget} className="w-full bg-amber-600 hover:bg-amber-700">
                Save Targets
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Today's Sales Table with Tabs */}
      <Tabs defaultValue="sales" className="mt-0">
        <TabsList className="bg-slate-700 mb-4 flex-wrap h-auto gap-1">
          <TabsTrigger value="sales" className="data-[state=active]:bg-emerald-600 text-white" data-testid="sales-tab">My Sales</TabsTrigger>
          <TabsTrigger value="parts-catalog" className="data-[state=active]:bg-cyan-600 text-white" data-testid="parts-catalog-tab">Parts Catalog</TabsTrigger>
        </TabsList>

        <TabsContent value="sales">
          <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-400" />
            TODAY'S SALES
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todaySales.length === 0 ? (
            <p className="text-slate-400 text-center py-8">
              {isSalesman
                ? 'No sales rung up on your behalf yet today.'
                : 'No sales recorded today. Click "Add Sale" to get started!'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Part</TableHead>
                  <TableHead className="text-slate-400 text-right">Qty</TableHead>
                  <TableHead className="text-slate-400 text-right">Total</TableHead>
                  <TableHead className="text-slate-400 text-center">Method</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {todaySales.map((sale, idx) => {
                  const pm = (sale.payment_method || 'cash').toLowerCase();
                  const pmStyles = { cash: 'bg-emerald-600', online: 'bg-blue-600', eft: 'bg-purple-600', card: 'bg-amber-600' };
                  return (
                    <TableRow key={idx} className="border-slate-700">
                      <TableCell className="text-white">{sale.part_description}</TableCell>
                      <TableCell className="text-white text-right">{sale.quantity}</TableCell>
                      <TableCell className="text-emerald-400 text-right font-bold">{formatCurrency(sale.total)}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold text-white ${pmStyles[pm] || 'bg-slate-700'}`}>{pm.toUpperCase()}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="border-slate-700 bg-slate-700/50">
                  <TableCell colSpan={2} className="text-white font-bold">MY TOTAL:</TableCell>
                  <TableCell className="text-emerald-400 text-right font-bold text-lg">{formatCurrency(dashboardData?.today_total)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="parts-catalog">
          <PartsCatalogPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
